export type EntityId = string | number;

export type ChildResourceState<T> = {
  loading?: boolean;
  error?: unknown;
  data?: T | null;
  [key: string]: unknown;
};

export type ChildResourceStateMap<T> = Record<string, ChildResourceState<T>>;

type SetChildResourceState<T> = (
  updater: (prev: ChildResourceStateMap<T>) => ChildResourceStateMap<T>
) => void;

export type ChildResourceLoaderOptions<T> = {
  request: (childId: EntityId) => Promise<T | null>;
  setState: SetChildResourceState<T>;
  resolveError?: (error: unknown, childId: EntityId) => T | null | undefined;
  keyFor?: (childId: EntityId) => string;
};

export type LoadChildResourceOptions = {
  force?: boolean;
};

export type ChildResourceLoader = {
  load: (childId: EntityId, options?: LoadChildResourceOptions) => Promise<void>;
  markLoaded: (childId: EntityId) => void;
  reset: (childId?: EntityId) => void;
};

const defaultKeyFor = (childId: EntityId) => String(childId);

export const createChildResourceLoader = <T,>({
  request,
  setState,
  resolveError,
  keyFor = defaultKeyFor,
}: ChildResourceLoaderOptions<T>): ChildResourceLoader => {
  const loaded = new Set<string>();
  const inFlight = new Map<string, Promise<void>>();

  const load = (childId: EntityId, options: LoadChildResourceOptions = {}) => {
    const key = keyFor(childId);
    if (!key) return Promise.resolve();

    if (!options.force) {
      const pending = inFlight.get(key);
      if (pending) return pending;
      if (loaded.has(key)) return Promise.resolve();
    } else {
      loaded.delete(key);
    }

    setState((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), loading: true, error: null },
    }));

    const promise = request(childId)
      .then((data) => {
        loaded.add(key);
        setState((prev) => ({
          ...prev,
          [key]: { ...(prev[key] || {}), loading: false, error: null, data },
        }));
      })
      .catch((error) => {
        const resolved = resolveError?.(error, childId);
        loaded.add(key);
        setState((prev) => ({
          ...prev,
          [key]:
            resolved !== undefined
              ? { ...(prev[key] || {}), loading: false, error: null, data: resolved }
              : { ...(prev[key] || {}), loading: false, error },
        }));
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, promise);
    return promise;
  };

  const markLoaded = (childId: EntityId) => {
    const key = keyFor(childId);
    if (key) {
      loaded.add(key);
      inFlight.delete(key);
    }
  };

  const reset = (childId?: EntityId) => {
    if (childId == null) {
      loaded.clear();
      inFlight.clear();
      return;
    }

    const key = keyFor(childId);
    loaded.delete(key);
    inFlight.delete(key);
  };

  return { load, markLoaded, reset };
};
